import React, { useCallback, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { IconButton, MenuItem, TextField } from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import EditIcon from '@material-ui/icons/Edit'
import {
  SourceQueryDisplay,
  TablesDisplay,
  ColumnsDisplay
} from '../configurationDisplay'
import BasicTable from '../../../../../../atoms/BasicTable'
import CodeTag from '../../../../../../atoms/CodeTag'
import RoundedModal from '../../../../../../molecules/RoundedModal'
import Switch from '../../../../../../molecules/Switch'
import SydButton from '../../../../../../commonDesign/Button'
import SydLabel from '../../../../../../commonDesign/SydLabel'
import PredicatesEditor from './PredicatesEditor'
import ScopePredicateEditor from './ScopePredicateEditor'
import EditPurposeDqlModal from './EditPurposeDqlModal'
import RenamePurposeModal from './RenamePurposeModal'

function buildDefaultLatch (dateTable) {
  return { parameterName: 'snapshotDate', dateColumn: 'snapshotDate', dateTable, limit: 100, defaultBehavior: 'latest' }
}

const ALL_PERIODS = ['day', 'week', 'month', 'quarter', 'year']

function buildDefaultTimeseriesLatch (dateTable) {
  return {
    periodParameter: 'period',
    rangeParameter: 'dateRange',
    periodColumn: 'period',
    dateColumn: 'periodStart',
    dateTable,
    supportedPeriods: ['day', 'month', 'quarter', 'year'],
    defaultPeriod: 'month',
    defaultBehavior: 'latest',
    defaultRange: null,
    limit: 100
  }
}

function buildDefaultRangeFor (kind) {
  switch (kind) {
    case 'absolute':
      return { kind: 'absolute', from: '', to: '' }
    case 'trailing':
      return { kind: 'trailing', asOfDate: '', periods: 12, excludeCurrentPeriod: false }
    case 'calendarYear':
      return { kind: 'calendarYear', calendarYear: 'current' }
    default:
      return null
  }
}

function PurposeDetailPanel ({ purposeCode, purposeData, onUpdatePurpose, onAnalyzeDql }) {
  const [editModal, setEditModal] = useState({ open: false, index: null, predicate: null })
  const [editDqlOpen, setEditDqlOpen] = useState(false)
  const [renameOpen, setRenameOpen] = useState(false)

  const handleAddPredicate = useCallback(() => {
    setEditModal({ open: true, index: null, predicate: null })
  }, [])

  const handleEditPredicate = useCallback((index) => {
    if (!purposeData) return
    setEditModal({
      open: true,
      index,
      predicate: purposeData.subordinatePredicates[index]
    })
  }, [purposeData])

  const handleCloseModal = useCallback(() => {
    setEditModal({ open: false, index: null, predicate: null })
  }, [])

  const handleSavePredicate = useCallback((saved) => {
    if (!purposeCode || !purposeData) return
    const current = purposeData.subordinatePredicates || []

    if (editModal.index !== null) {
      const updated = current.map((p, i) => i === editModal.index ? saved : p)
      onUpdatePurpose(purposeCode, { ...purposeData, subordinatePredicates: updated })
    } else {
      onUpdatePurpose(purposeCode, { ...purposeData, subordinatePredicates: [...current, saved] })
    }
    handleCloseModal()
  }, [purposeCode, purposeData, editModal.index, onUpdatePurpose, handleCloseModal])

  const handleRemovePredicate = useCallback((index) => {
    if (!purposeCode || !purposeData) return
    const updated = (purposeData.subordinatePredicates || []).filter((_, i) => i !== index)
    onUpdatePurpose(purposeCode, { ...purposeData, subordinatePredicates: updated })
  }, [purposeCode, purposeData, onUpdatePurpose])

  const [scopeModal, setScopeModal] = useState({ open: false, index: null, predicate: null })

  const handleAddScopePredicate = useCallback(() => {
    setScopeModal({ open: true, index: null, predicate: null })
  }, [])

  const handleEditScopePredicate = useCallback((index) => {
    if (!purposeData) return
    setScopeModal({
      open: true,
      index,
      predicate: (purposeData.scopePredicates || [])[index]
    })
  }, [purposeData])

  const handleCloseScopeModal = useCallback(() => {
    setScopeModal({ open: false, index: null, predicate: null })
  }, [])

  const handleSaveScopePredicate = useCallback((saved) => {
    if (!purposeCode || !purposeData) return
    const current = purposeData.scopePredicates || []

    if (scopeModal.index !== null) {
      const updated = current.map((p, i) => i === scopeModal.index ? saved : p)
      onUpdatePurpose(purposeCode, { ...purposeData, scopePredicates: updated })
    } else {
      onUpdatePurpose(purposeCode, { ...purposeData, scopePredicates: [...current, saved] })
    }
    handleCloseScopeModal()
  }, [purposeCode, purposeData, scopeModal.index, onUpdatePurpose, handleCloseScopeModal])

  const handleRemoveScopePredicate = useCallback((index) => {
    if (!purposeCode || !purposeData) return
    const updated = (purposeData.scopePredicates || []).filter((_, i) => i !== index)
    onUpdatePurpose(purposeCode, { ...purposeData, scopePredicates: updated })
  }, [purposeCode, purposeData, onUpdatePurpose])

  const latchDateTable = useMemo(() => {
    const mainTable = (purposeData?.tables || []).find(t => t.purpose === 'main')
    if (!mainTable) return ''
    return mainTable.schema ? `${mainTable.schema}.${mainTable.tableCode}` : mainTable.tableCode
  }, [purposeData])

  const handleToggleLatch = useCallback((e) => {
    if (!purposeCode || !purposeData) return
    const enabled = e.target.checked
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      latch: enabled ? buildDefaultLatch(latchDateTable) : null
    })
  }, [purposeCode, purposeData, onUpdatePurpose, latchDateTable])

  const handleAutoConfigureLatch = useCallback(() => {
    if (!purposeCode || !purposeData) return
    if (!latchDateTable) {
      window.alert('Unable to auto-configure: no main table found in this purpose. Configure the latch fields manually.')
      return
    }
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      latch: buildDefaultLatch(latchDateTable)
    })
  }, [purposeCode, purposeData, onUpdatePurpose, latchDateTable])

  const handleLatchFieldChange = useCallback((field, value) => {
    if (!purposeCode || !purposeData?.latch) return
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      latch: { ...purposeData.latch, [field]: value }
    })
  }, [purposeCode, purposeData, onUpdatePurpose])

  const handleToggleTimeseriesLatch = useCallback((e) => {
    if (!purposeCode || !purposeData) return
    const enabled = e.target.checked
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      timeseriesLatch: enabled ? buildDefaultTimeseriesLatch(latchDateTable) : null
    })
  }, [purposeCode, purposeData, onUpdatePurpose, latchDateTable])

  const handleTimeseriesLatchFieldChange = useCallback((field, value) => {
    if (!purposeCode || !purposeData?.timeseriesLatch) return
    const next = { ...purposeData.timeseriesLatch, [field]: value }

    // Behaviour-driven defaults: when switching to 'trailing' make sure a
    // defaultRange exists so the form can render its controls; otherwise drop it.
    if (field === 'defaultBehavior') {
      if (value === 'trailing' && !next.defaultRange) {
        next.defaultRange = buildDefaultRangeFor('trailing')
      } else if (value !== 'trailing') {
        next.defaultRange = null
      }
    }

    // Keep defaultPeriod consistent with supportedPeriods, no matter which
    // field the user just changed. Most often this fires from the chip-toggle
    // path narrowing supportedPeriods, but the same guard catches edits via
    // other field handlers (or a hot-edit of the raw config via JSON) before
    // the backend's `.custom()` cross-field check rejects on save.
    const periods = Array.isArray(next.supportedPeriods) ? next.supportedPeriods : []
    if (next.defaultPeriod && periods.length > 0 && !periods.includes(next.defaultPeriod)) {
      next.defaultPeriod = periods[0]
    }

    onUpdatePurpose(purposeCode, { ...purposeData, timeseriesLatch: next })
  }, [purposeCode, purposeData, onUpdatePurpose])

  const handleTimeseriesLatchDefaultRangeChange = useCallback((field, value) => {
    if (!purposeCode || !purposeData?.timeseriesLatch?.defaultRange) return
    const dr = purposeData.timeseriesLatch.defaultRange
    const nextRange = field === 'kind' ? buildDefaultRangeFor(value) : { ...dr, [field]: value }
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      timeseriesLatch: { ...purposeData.timeseriesLatch, defaultRange: nextRange }
    })
  }, [purposeCode, purposeData, onUpdatePurpose])

  const handleTogglePeriodChip = useCallback((period) => {
    if (!purposeCode || !purposeData?.timeseriesLatch) return
    const current = new Set(purposeData.timeseriesLatch.supportedPeriods || [])
    if (current.has(period)) {
      current.delete(period)
    } else {
      current.add(period)
    }
    // Preserve canonical ordering across the toggles.
    const ordered = ALL_PERIODS.filter(p => current.has(p))
    if (ordered.length === 0) return
    handleTimeseriesLatchFieldChange('supportedPeriods', ordered)
  }, [purposeCode, purposeData, handleTimeseriesLatchFieldChange])

  const handleOpenInQueryTool = useCallback(() => {
    if (!purposeData?.sourceQuery) return
    localStorage.setItem('denaliQueryToolDql', purposeData.sourceQuery)
    window.open('/admin/denali/analysis/catalog/query?fromAssistant=1', '_blank')
  }, [purposeData])

  const handleRename = useCallback((newName) => {
    if (!purposeCode || !purposeData) return
    onUpdatePurpose(purposeCode, { ...purposeData, displayName: newName })
    setRenameOpen(false)
  }, [purposeCode, purposeData, onUpdatePurpose])

  const handleUpdateColumn = useCallback((colIndex, field, value) => {
    if (!purposeCode || !purposeData) return
    const updatedColumns = purposeData.columns.map((col, i) =>
      i === colIndex ? { ...col, [field]: value } : col
    )
    onUpdatePurpose(purposeCode, { ...purposeData, columns: updatedColumns })
  }, [purposeCode, purposeData, onUpdatePurpose])

  const handlePaginationChange = useCallback((field, value) => {
    if (!purposeCode || !purposeData) return
    const current = purposeData.pagination || { defaultPageSize: 100, maxPageSize: 1000 }
    const numeric = Number.parseInt(value, 10)
    const next = Number.isFinite(numeric) && numeric > 0 ? numeric : current[field]
    onUpdatePurpose(purposeCode, {
      ...purposeData,
      pagination: { ...current, [field]: next }
    })
  }, [purposeCode, purposeData, onUpdatePurpose])

  if (!purposeCode || !purposeData) {
    return (
      <div style={{
        padding: '32px',
        textAlign: 'center',
        color: 'var(--text-color-subtle)',
        paddingLeft: '32px'
      }}
      >
        <p>Select a purpose to view details</p>
      </div>
    )
  }

  const subordinatePredicates = purposeData.subordinatePredicates || []

  return (
    <div style={{ paddingLeft: '24px', overflowY: 'auto' }}>
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <h3 style={{ marginBottom: '4px' }}>{purposeData.displayName}</h3>
          <IconButton size='small' onClick={() => setRenameOpen(true)} aria-label='Rename purpose' title='Rename'>
            <EditIcon fontSize='small' />
          </IconButton>
        </div>
        <div style={{ fontSize: '0.875rem', color: 'var(--text-color-subtle)' }}>
          Purpose Code: {purposeCode}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <SydButton size='xs' variant='outline' icon='openInNew' onClick={handleOpenInQueryTool}>
          Open in Query Tool
        </SydButton>
        <SydButton size='xs' variant='outline' icon='edit' onClick={() => setEditDqlOpen(true)}>
          Edit Query
        </SydButton>
        {onAnalyzeDql && (
          <SydButton
            size='xs'
            variant='outline'
            icon='queryStats'
            onClick={() => onAnalyzeDql(purposeCode, purposeData?.sourceQuery || '')}
            title='Run DQL diagnostics on this purpose without leaving the studio.'
          >
            Diagnose
          </SydButton>
        )}
      </div>
      <SourceQueryDisplay sourceQuery={purposeData.sourceQuery} />
      <TablesDisplay tables={purposeData.tables} />
      <ColumnsDisplay columns={purposeData.columns} onUpdateColumn={handleUpdateColumn} />

      {/* Predicates */}
      <div style={{ marginBottom: '16px' }}>
        <h4>Predicates</h4>

        {/* Main Predicate (read-only) */}
        <div style={{ marginBottom: '12px' }}>
          <h5>Main Predicate</h5>
          <CodeTag>
            {purposeData.masterPredicate?.expression || 'None'}
          </CodeTag>
          {purposeData.masterPredicate?.requiredParams?.length > 0
            ? (
              <p style={{ fontSize: 'var(--text-size-sm)', color: 'var(--text-color-subtle)', marginTop: '4px' }}>
                Parameters: {purposeData.masterPredicate?.requiredParams.join(', ')}
              </p>
            )
            : null}
        </div>

        {/* Filter Predicates */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <h5 style={{ margin: 0 }}>Filter Predicates ({subordinatePredicates.length})</h5>
          <SydButton size='sm' icon='add' onClick={handleAddPredicate}>
            Add Filter
          </SydButton>
        </div>

        {subordinatePredicates.length > 0 ? (
          <BasicTable className='opal-presentation-table zebra-rows'>
            <thead className='opt-thead'>
              <tr className='opt-tr'>
                <th className='opt-th'>Code</th>
                <th className='opt-th'>Description</th>
                <th className='opt-th'>Expression</th>
                <th className='opt-th' style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody className='opt-tbody'>
              {subordinatePredicates.map((pred, idx) => (
                <tr className='opt-tr' key={pred.code || idx}>
                  <td className='opt-td'>{pred.code}</td>
                  <td className='opt-td'>{pred.description || '-'}</td>
                  <td className='opt-td'>
                    <CodeTag>{pred.expression}</CodeTag>
                  </td>
                  <td className='opt-td'>
                    <div className='flex flex-row gap-sm'>
                      <IconButton size='small' onClick={() => handleEditPredicate(idx)} aria-label='Edit predicate' title='Edit predicate'>
                        <EditIcon fontSize='small' />
                      </IconButton>
                      <IconButton size='small' onClick={() => handleRemovePredicate(idx)} aria-label='Delete predicate' title='Delete predicate'>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </BasicTable>
        ) : (
          <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
            No filter predicates configured.
          </p>
        )}
      </div>

      <RoundedModal
        title={editModal.index !== null ? 'Edit Filter Predicate' : 'Add Filter Predicate'}
        open={editModal.open}
        onClose={handleCloseModal}
        size='medium'
        position='top'
        disablePortal={false}
      >
        {editModal.open
          ? (
            <PredicatesEditor
              predicate={editModal.predicate}
              onSave={handleSavePredicate}
              onCancel={handleCloseModal}
            />
          )
          : <div />}
      </RoundedModal>

      {/* Scope Predicates */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <h5 style={{ margin: 0 }}>Scope Predicates ({(purposeData.scopePredicates || []).length})</h5>
          <SydButton size='sm' icon='add' onClick={handleAddScopePredicate}>
            Add Scope
          </SydButton>
        </div>

        {(purposeData.scopePredicates || []).length > 0 ? (
          <BasicTable className='opal-presentation-table zebra-rows'>
            <thead className='opt-thead'>
              <tr className='opt-tr'>
                <th className='opt-th'>Scope Name</th>
                <th className='opt-th'>Code</th>
                <th className='opt-th'>Type</th>
                <th className='opt-th'>Details</th>
                <th className='opt-th' style={{ width: '80px' }} />
              </tr>
            </thead>
            <tbody className='opt-tbody'>
              {(purposeData.scopePredicates || []).map((sp, idx) => (
                <tr className='opt-tr' key={sp.code || idx}>
                  <td className='opt-td'>{sp.scopeType}</td>
                  <td className='opt-td'>{sp.code}</td>
                  <td className='opt-td'>{sp.type === 'expression' ? 'Expression' : 'Membership'}</td>
                  <td className='opt-td'>
                    {sp.type === 'expression'
                      ? <CodeTag>{sp.expression}</CodeTag>
                      : <span>Path: {(sp.path || []).join(' → ')} · Column: {sp.column}</span>}
                  </td>
                  <td className='opt-td'>
                    <div className='flex flex-row gap-sm'>
                      <IconButton size='small' onClick={() => handleEditScopePredicate(idx)} aria-label='Edit scope predicate' title='Edit scope predicate'>
                        <EditIcon fontSize='small' />
                      </IconButton>
                      <IconButton size='small' onClick={() => handleRemoveScopePredicate(idx)} aria-label='Delete scope predicate' title='Delete scope predicate'>
                        <DeleteIcon fontSize='small' />
                      </IconButton>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </BasicTable>
        ) : (
          <p style={{ color: 'var(--text-color-subtle)', fontSize: '0.875rem' }}>
            No scope predicates configured. Add scope predicates to allow this purpose to participate in page-level scope filtering.
          </p>
        )}
      </div>

      <RoundedModal
        title={scopeModal.index !== null ? 'Edit Scope Predicate' : 'Add Scope Predicate'}
        open={scopeModal.open}
        onClose={handleCloseScopeModal}
        size='large'
        position='top'
        disablePortal={false}
      >
        {scopeModal.open
          ? (
            <ScopePredicateEditor
              predicate={scopeModal.predicate}
              columns={purposeData?.columns || []}
              sourceQuery={purposeData?.sourceQuery || ''}
              onSave={handleSaveScopePredicate}
              onCancel={handleCloseScopeModal}
            />
          )
          : <div />}
      </RoundedModal>

      {/* Snapshot Latch */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <h4 style={{ margin: 0 }}>Snapshot Latch</h4>
          <Switch
            checked={!!purposeData.latch}
            onChange={handleToggleLatch}
          />
          {purposeData.latch ? (
            <SydButton size='xs' variant='outline' onClick={handleAutoConfigureLatch}>
              Auto-configure
            </SydButton>
          ) : null}
        </div>
        {purposeData.latch
          ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <SydLabel label='Parameter Name' helperText='Key used when passing the latch date in parameters (e.g., snapshotDate)'>
                <TextField
                  value={purposeData.latch.parameterName || ''}
                  onChange={(e) => handleLatchFieldChange('parameterName', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='snapshotDate'
                />
              </SydLabel>
              <SydLabel label='Date Column' helperText='Column used to filter results by snapshot date and to query available dates (e.g., snapshotDate)'>
                <TextField
                  value={purposeData.latch.dateColumn || ''}
                  onChange={(e) => handleLatchFieldChange('dateColumn', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='snapshotDate'
                />
              </SydLabel>
              <SydLabel label='Date Table' helperText='Fully qualified table (e.g., db.account_performance)'>
                <TextField
                  value={purposeData.latch.dateTable || ''}
                  onChange={(e) => handleLatchFieldChange('dateTable', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='db.account_performance'
                />
              </SydLabel>
              <SydLabel label='Limit' helperText='Max dates to show in selector'>
                <TextField
                  value={purposeData.latch.limit || 100}
                  onChange={(e) => handleLatchFieldChange('limit', parseInt(e.target.value, 10) || 100)}
                  size='small'
                  fullWidth
                  type='number'
                />
              </SydLabel>
              <SydLabel label='When date not provided' helperText='Behavior when the latch date parameter is omitted from the request'>
                <TextField
                  select
                  value={purposeData.latch.defaultBehavior || 'latest'}
                  onChange={(e) => handleLatchFieldChange('defaultBehavior', e.target.value)}
                  size='small'
                  fullWidth
                >
                  <MenuItem value='latest'>Default to latest snapshot</MenuItem>
                  <MenuItem value='required'>Require it (error)</MenuItem>
                </TextField>
              </SydLabel>
            </div>
          )
          : null}
      </div>

      {/* Time Series Latch */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
          <h4 style={{ margin: 0 }}>Time Series Latch</h4>
          <Switch
            checked={!!purposeData.timeseriesLatch}
            onChange={handleToggleTimeseriesLatch}
          />
        </div>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-color-subtle)', marginTop: 0, marginBottom: '8px' }}>
          Auto-injects <code>period = :period</code> and <code>dateColumn BETWEEN :from AND :to</code> onto
          the AST WHERE at execution time. Available period bounds are queried directly from the configured table.
        </p>
        {purposeData.timeseriesLatch
          ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <SydLabel label='Period Parameter Name' helperText='Key used when passing the period in parameters (e.g., period)'>
                <TextField
                  value={purposeData.timeseriesLatch.periodParameter || ''}
                  onChange={(e) => handleTimeseriesLatchFieldChange('periodParameter', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='period'
                />
              </SydLabel>
              <SydLabel label='Range Parameter Name' helperText='Key used when passing the {from,to} range (e.g., dateRange)'>
                <TextField
                  value={purposeData.timeseriesLatch.rangeParameter || ''}
                  onChange={(e) => handleTimeseriesLatchFieldChange('rangeParameter', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='dateRange'
                />
              </SydLabel>
              <SydLabel label='Period Column' helperText='Column filtered by period value (e.g., period or b.period)'>
                <TextField
                  value={purposeData.timeseriesLatch.periodColumn || ''}
                  onChange={(e) => handleTimeseriesLatchFieldChange('periodColumn', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='period'
                />
              </SydLabel>
              <SydLabel label='Date Column' helperText='Column filtered by date range (e.g., periodStart or b.periodStart)'>
                <TextField
                  value={purposeData.timeseriesLatch.dateColumn || ''}
                  onChange={(e) => handleTimeseriesLatchFieldChange('dateColumn', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='periodStart'
                />
              </SydLabel>
              <SydLabel label='Date Table' helperText='Fully qualified table queried for available period bounds (e.g., dataExport.balances)'>
                <TextField
                  value={purposeData.timeseriesLatch.dateTable || ''}
                  onChange={(e) => handleTimeseriesLatchFieldChange('dateTable', e.target.value)}
                  size='small'
                  fullWidth
                  placeholder='dataExport.balances'
                />
              </SydLabel>
              <SydLabel label='Limit' helperText='Row cap on available-dates query results'>
                <TextField
                  value={purposeData.timeseriesLatch.limit || 100}
                  onChange={(e) => handleTimeseriesLatchFieldChange('limit', parseInt(e.target.value, 10) || 100)}
                  size='small'
                  fullWidth
                  type='number'
                />
              </SydLabel>
              <SydLabel label='Supported Periods' helperText='Periods this purpose can be queried by'>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center', minHeight: '40px' }}>
                  {ALL_PERIODS.map(p => {
                    const active = (purposeData.timeseriesLatch.supportedPeriods || []).includes(p)
                    return (
                      <SydButton
                        key={p}
                        size='xs'
                        variant={active ? 'primary' : 'outline'}
                        onClick={() => handleTogglePeriodChip(p)}
                      >
                        {p}
                      </SydButton>
                    )
                  })}
                </div>
              </SydLabel>
              <SydLabel label='Default Period' helperText='Period used when the caller does not provide one'>
                <TextField
                  select
                  value={purposeData.timeseriesLatch.defaultPeriod || ''}
                  onChange={(e) => handleTimeseriesLatchFieldChange('defaultPeriod', e.target.value)}
                  size='small'
                  fullWidth
                >
                  {(purposeData.timeseriesLatch.supportedPeriods || []).map(p => (
                    <MenuItem key={p} value={p}>{p}</MenuItem>
                  ))}
                </TextField>
              </SydLabel>
              <SydLabel label='When range not provided' helperText='Behavior when the range parameter is omitted from the request'>
                <TextField
                  select
                  value={purposeData.timeseriesLatch.defaultBehavior || 'latest'}
                  onChange={(e) => handleTimeseriesLatchFieldChange('defaultBehavior', e.target.value)}
                  size='small'
                  fullWidth
                >
                  <MenuItem value='latest'>Default to latest single period</MenuItem>
                  <MenuItem value='trailing'>Use configured default range</MenuItem>
                  <MenuItem value='required'>Require it (error)</MenuItem>
                </TextField>
              </SydLabel>
              {purposeData.timeseriesLatch.defaultBehavior === 'trailing' && purposeData.timeseriesLatch.defaultRange
                ? (
                  <>
                    <SydLabel label='Default Range Kind' helperText='Shape of the default range used when no value is supplied'>
                      <TextField
                        select
                        value={purposeData.timeseriesLatch.defaultRange.kind || 'trailing'}
                        onChange={(e) => handleTimeseriesLatchDefaultRangeChange('kind', e.target.value)}
                        size='small'
                        fullWidth
                      >
                        <MenuItem value='absolute'>Absolute (from / to)</MenuItem>
                        <MenuItem value='trailing'>Trailing periods (asOfDate / periods)</MenuItem>
                        <MenuItem value='calendarYear'>Calendar year</MenuItem>
                      </TextField>
                    </SydLabel>
                    <div />
                    {purposeData.timeseriesLatch.defaultRange.kind === 'absolute' && (
                      <>
                        <SydLabel label='From'>
                          <TextField
                            value={purposeData.timeseriesLatch.defaultRange.from || ''}
                            onChange={(e) => handleTimeseriesLatchDefaultRangeChange('from', e.target.value)}
                            size='small'
                            fullWidth
                            placeholder='YYYY-MM-DD'
                          />
                        </SydLabel>
                        <SydLabel label='To'>
                          <TextField
                            value={purposeData.timeseriesLatch.defaultRange.to || ''}
                            onChange={(e) => handleTimeseriesLatchDefaultRangeChange('to', e.target.value)}
                            size='small'
                            fullWidth
                            placeholder='YYYY-MM-DD'
                          />
                        </SydLabel>
                      </>
                    )}
                    {purposeData.timeseriesLatch.defaultRange.kind === 'trailing' && (
                      <>
                        <SydLabel label='As Of Date'>
                          <TextField
                            value={purposeData.timeseriesLatch.defaultRange.asOfDate || ''}
                            onChange={(e) => handleTimeseriesLatchDefaultRangeChange('asOfDate', e.target.value)}
                            size='small'
                            fullWidth
                            placeholder='YYYY-MM-DD'
                          />
                        </SydLabel>
                        <SydLabel label='Periods' helperText='Number of periods to look back'>
                          <TextField
                            value={purposeData.timeseriesLatch.defaultRange.periods || 1}
                            onChange={(e) => handleTimeseriesLatchDefaultRangeChange('periods', parseInt(e.target.value, 10) || 1)}
                            size='small'
                            fullWidth
                            type='number'
                            inputProps={{ min: 1, max: 100 }}
                          />
                        </SydLabel>
                      </>
                    )}
                    {purposeData.timeseriesLatch.defaultRange.kind === 'calendarYear' && (
                      <SydLabel label='Calendar Year' helperText='Year (e.g., 2024) or the string "current"'>
                        <TextField
                          value={purposeData.timeseriesLatch.defaultRange.calendarYear ?? ''}
                          onChange={(e) => handleTimeseriesLatchDefaultRangeChange('calendarYear', e.target.value)}
                          size='small'
                          fullWidth
                          placeholder='current'
                        />
                      </SydLabel>
                    )}
                  </>
                )
                : null}
            </div>
          )
          : null}
      </div>

      {/* Pagination */}
      <div style={{ marginBottom: '16px' }}>
        <h4>Pagination</h4>
        <p style={{ fontSize: '0.8125rem', color: 'var(--text-color-subtle)', marginTop: 0, marginBottom: '8px' }}>
          Default page size is what callers receive without an override; max page size caps any
          override they request. Note: a <code>LIMIT</code> clause inside the source DQL is
          currently <strong>stripped at execution time</strong> by the pipeline, so use
          pagination here to express row caps rather than baking <code>LIMIT</code> into the query.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
          <SydLabel label='Default Page Size' helperText='Used when caller does not specify a page size'>
            <TextField
              value={(purposeData.pagination?.defaultPageSize) ?? 100}
              onChange={(e) => handlePaginationChange('defaultPageSize', e.target.value)}
              size='small'
              fullWidth
              type='number'
              inputProps={{ min: 1, max: 1000 }}
            />
          </SydLabel>
          <SydLabel label='Max Page Size' helperText='Hard cap on caller-requested page size (1\u20131000)'>
            <TextField
              value={(purposeData.pagination?.maxPageSize) ?? 1000}
              onChange={(e) => handlePaginationChange('maxPageSize', e.target.value)}
              size='small'
              fullWidth
              type='number'
              inputProps={{ min: 1, max: 1000 }}
            />
          </SydLabel>
        </div>
      </div>

      <EditPurposeDqlModal
        open={editDqlOpen}
        onClose={() => setEditDqlOpen(false)}
        purposeCode={purposeCode}
        purposeData={purposeData}
        onUpdate={onUpdatePurpose}
      />

      <RenamePurposeModal
        open={renameOpen}
        onClose={() => setRenameOpen(false)}
        currentName={purposeData.displayName}
        onSave={handleRename}
      />
    </div>
  )
}

PurposeDetailPanel.propTypes = {
  purposeCode: PropTypes.string,
  purposeData: PropTypes.object,
  onUpdatePurpose: PropTypes.func,
  onAnalyzeDql: PropTypes.func
}

PurposeDetailPanel.defaultProps = {
  onUpdatePurpose: () => {}
}

export default PurposeDetailPanel
